import {Box, Button, ButtonGroup, Text, Textarea, useDialogContext, VStack} from '@chakra-ui/react'
import {DialogBody, DialogFooter, DialogHeader} from '@cypro/ui/components'
import {formatIsoDate} from '@/shared/utils/dates'
import {useTranslate} from '@tolgee/react'
import {useState} from 'react'

export interface MovedCycleChange {
  cycle: number
  fromDate: string
  toDate: string
  shiftedCount: number
  validationsRemoved: number
}

export interface DeletedCycleChange {
  originalCycle: number
  prescriptionsCancelled: number
  renumbered: {from: number; to: number}[]
}

export interface AddedCycleChange {
  cycle: number
  startDate: string
  prescriptionsCount: number
}

export interface ProtocolChangeSummary {
  moved: MovedCycleChange[]
  deleted: DeletedCycleChange[]
  added: AddedCycleChange[]
}

export interface SaveProtocolChangesDialogProps {
  changes: ProtocolChangeSummary
  onConfirm: (reason: string) => void | Promise<void>
}

export function SaveProtocolChangesDialog({changes, onConfirm}: SaveProtocolChangesDialogProps) {
  const {t} = useTranslate('common')
  const context = useDialogContext()
  const [reason, setReason] = useState('')

  function onBack() {
    context.setOpen(false)
  }

  function onSave() {
    context.setOpen(false)
    void onConfirm(reason)
  }

  return (
    <>
      <DialogHeader>
        <Text fontSize="lg" fontWeight="semibold">
          {t('save_protocol_changes', 'Save protocol changes?')}
        </Text>
      </DialogHeader>

      <DialogBody minHeight="none">
        <VStack align="stretch" gap="4" width="100%">
          <Box bg="bg.subtle" borderRadius="md" borderWidth="1px" borderColor="border" p="4" maxHeight="20rem" overflowY="auto">
            <VStack align="stretch" gap="4">
              {changes.moved.map(change => (
                <Box key={`moved-${change.cycle}`}>
                  <Text fontWeight="semibold">{t('cycle_x_moved', {cycle: change.cycle})}</Text>
                  <Text fontSize="sm" color="fg.muted">
                    {t('protocol.start_date')}: {formatIsoDate(change.fromDate)} → {formatIsoDate(change.toDate)}
                  </Text>
                  <Text fontSize="sm" color="fg.muted">
                    {t('prescriptions_shifted')}: {change.shiftedCount}
                  </Text>
                  {change.validationsRemoved > 0 && (
                    <Text fontSize="sm" color="fg.muted">
                      {t('validations_removed')}: {change.validationsRemoved}
                    </Text>
                  )}
                </Box>
              ))}
              {changes.deleted.map(change => (
                <Box key={`deleted-${change.originalCycle}`}>
                  <Text fontWeight="semibold">
                    {t('cycle_x_deleted', {cycle: change.originalCycle})}
                  </Text>
                  <Text fontSize="sm" color="fg.muted">
                    {t('prescriptions_cancelled')}: {change.prescriptionsCancelled}
                  </Text>
                  {change.renumbered.map(pair => (
                    <Text key={`${pair.from}-${pair.to}`} fontSize="sm" color="fg.muted">
                      {t('renumbered')}: {t('cycle')} {pair.from} → {t('cycle')} {pair.to}
                    </Text>
                  ))}
                </Box>
              ))}
              {changes.added.map(change => (
                <Box key={`added-${change.cycle}`}>
                  <Text fontWeight="semibold">{t('cycle_x_added', {cycle: change.cycle})}</Text>
                  <Text fontSize="sm" color="fg.muted">
                    {t('protocol.start_date')}: {formatIsoDate(change.startDate)}
                  </Text>
                  <Text fontSize="sm" color="fg.muted">
                    {t('prescriptions')}: {change.prescriptionsCount}
                  </Text>
                </Box>
              ))}
            </VStack>
          </Box>

          <Box>
            <Text fontWeight="medium" marginBottom="2">
              {t('reason_for_change')}
            </Text>
            <Textarea
              placeholder={t('recorded_in_patient_history')}
              value={reason}
              onChange={e => {
                setReason(e.target.value)
              }}
              rows={3}
            />
          </Box>
        </VStack>
      </DialogBody>

      <DialogFooter>
        <ButtonGroup size="md" colorPalette="cyan" width="100%" justifyContent="flex-end">
          <Button variant="outline" colorPalette="gray" onClick={onBack}>
            {t('back')}
          </Button>
          <Button onClick={onSave}>{t('save_changes')}</Button>
        </ButtonGroup>
      </DialogFooter>
    </>
  )
}
